import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { ChevronLeft, ChevronRight, X, Trash2, Mail, CheckCircle } from 'lucide-react';
import { adminApi } from '../api/admin';
import AdminLayout from '../components/AdminLayout';

export default function AdminContact() {
  usePageTitle("Administration — Messages de contact — Lodiss");

  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => { loadMessages(); }, [page, statusFilter]);

  const loadMessages = () => {
    setLoading(true);
    adminApi.getContactMessages({ page, limit: 20, status: statusFilter || undefined })
      .then(res => { setMessages(res.items); setTotal(res.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await adminApi.updateContactMessage(id, { status });
      loadMessages();
      if (selectedMessage?.id === id) {
        setSelectedMessage({ ...selectedMessage, status });
      }
    } catch (err) { console.error('Failed to update:', err); }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminApi.deleteContactMessage(id);
      setDeleteConfirm(null);
      loadMessages();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const openMessage = async (msg: any) => {
    setSelectedMessage(msg);
    if (msg.status === 'unread') {
      await handleStatusChange(msg.id, 'read');
    }
  };

  const getStatusColor = (status: string) => ({
    unread: 'bg-red-50 text-red-700',
    read: 'bg-blue-50 text-blue-700',
    replied: 'bg-emerald-50 text-emerald-700',
  }[status] || 'bg-gray-50 text-gray-700');

  const totalPages = Math.ceil(total / 20);

  return (
    <AdminLayout>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Messages</h1>
        <div className="flex gap-4 mb-6">
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-4 py-2 border rounded-lg">
            <option value="">Tous statuts</option>
            <option value="unread">Non lu</option>
            <option value="read">Lu</option>
            <option value="replied">Répondu</option>
          </select>
        </div>
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">De</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Sujet</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Aperçu</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center"><div className="w-6 h-6 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
              ) : messages.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Aucun message</td></tr>
              ) : (
                messages.map(msg => (
                  <tr key={msg.id} className={`hover:bg-gray-50 ${msg.status === 'unread' ? 'bg-red-50/30' : ''}`}>
                    <td className="px-4 py-3 text-sm">
                      <p className="font-medium">{msg.name}</p>
                      <p className="text-xs text-gray-500">{msg.email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{msg.subject}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{msg.message}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(msg.status)}`}>{msg.status}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(msg.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openMessage(msg)} className="text-sm text-lodiss-red hover:underline">Voir</button>
                        <button onClick={() => setDeleteConfirm(msg.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">{(page - 1) * 20 + 1} - {Math.min(page * 20, total)} sur {total}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border rounded-lg disabled:opacity-50"><ChevronLeft size={18} /></button>
              <span className="text-sm">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border rounded-lg disabled:opacity-50"><ChevronRight size={18} /></button>
            </div>
          </div>
        )}
        {selectedMessage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">{selectedMessage.subject}</h2>
                <button onClick={() => setSelectedMessage(null)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-4 mb-4 pb-4 border-b">
                  <div className="flex-1">
                    <p className="font-medium">{selectedMessage.name}</p>
                    <p className="text-sm text-gray-500">{selectedMessage.email}</p>
                  </div>
                  <p className="text-sm text-gray-500">{new Date(selectedMessage.created_at).toLocaleString('fr-FR')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
              </div>
              <div className="flex justify-between items-center p-4 border-t">
                <div className="flex gap-2">
                  {selectedMessage.status !== 'replied' && (
                    <button
                      onClick={() => handleStatusChange(selectedMessage.id, 'replied')}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                    >
                      <CheckCircle size={16} />
                      Marquer comme répondu
                    </button>
                  )}
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <Mail size={16} />
                    Répondre
                  </a>
                </div>
                <button onClick={() => setSelectedMessage(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Fermer</button>
              </div>
            </div>
          </div>
        )}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md">
              <h3 className="text-lg font-bold mb-2">Confirmer la suppression</h3>
              <p className="text-gray-500 mb-4">Êtes-vous sûr de vouloir supprimer ce message ?</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Annuler</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">Supprimer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
